import React, { useMemo } from 'react';
import { X, FileText, Shield, Car, Eye } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

function ActiveContractsModal({ contracts, vehicleInsurances, intermediaries, onClose, onViewContract }) {
  const { t, fmt } = useFormat();
  const getIntermediary = (id) => intermediaries.find(i => i.id === id);
  // Les primes appartiennent à des filiales différentes : on CONVERTIT avant d'additionner.
  const { sumScope } = useOrgCurrency();

  const summary = useMemo(() => {
    const insurers = [...new Set(contracts.map(c => c.insurer_name).filter(Boolean))];
    const intermediaryNames = [...new Set(
      contracts.map(c => getIntermediary(c.intermediary_id)?.name).filter(Boolean)
    )];
    const totalVehicles = new Set(
      vehicleInsurances
        .filter(vi => contracts.some(c => c.id === vi.contract_id))
        .map(vi => vi.vehicle_id)
    ).size;
    const totalPremium = sumScope(
      vehicleInsurances.filter(vi => contracts.some(c => c.id === vi.contract_id)),
      vi => vi.premium_amount
    );
    const dates = contracts
      .map(c => c.expiry_date)
      .filter(Boolean)
      .sort();

    return { insurers, intermediaryNames, totalVehicles, totalPremium, dates };
  }, [contracts, vehicleInsurances, intermediaries, sumScope]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[800px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('insurance.activeContractsModal.title', { count: contracts.length })}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Executive Summary */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-blue-900 text-sm uppercase tracking-wide">{t('insurance.activeContractsModal.summary')}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-blue-600 font-medium">{t('insurance.activeContractsModal.insurers')}</span>
                <span className="text-blue-900">{summary.insurers.join(', ') || '—'}</span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">{t('insurance.activeContractsModal.coverage')}</span>
                <span className="text-blue-900">{t('insurance.activeContractsModal.coverageValue', { contracts: contracts.length, vehicles: summary.totalVehicles })}</span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">{t('insurance.activeContractsModal.expiries')}</span>
                <span className="text-blue-900">
                  {summary.dates.length > 0
                    ? `${fmt.date(summary.dates[0])} — ${fmt.date(summary.dates[summary.dates.length - 1])}`
                    : '—'}
                </span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">{t('insurance.activeContractsModal.totalPremium')}</span>
                <span className="text-blue-900 font-semibold">{summary.totalPremium.label}</span>
              </div>
              {summary.intermediaryNames.length > 0 && (
                <div className="col-span-2">
                  <span className="text-blue-600 font-medium">{t('insurance.activeContractsModal.intermediaries')}</span>
                  <span className="text-blue-900">{summary.intermediaryNames.join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-3 pr-4">{t('insurance.activeContractsModal.col.policy')}</th>
                  <th className="pb-3 pr-4">{t('insurance.activeContractsModal.col.insurer')}</th>
                  <th className="pb-3 pr-4">{t('insurance.activeContractsModal.col.intermediary')}</th>
                  <th className="pb-3 pr-4 text-center">{t('insurance.activeContractsModal.col.vehicles')}</th>
                  <th className="pb-3 pr-4">{t('insurance.activeContractsModal.col.expiry')}</th>
                  <th className="pb-3 pr-4 text-right">{t('insurance.activeContractsModal.col.premium')}</th>
                  <th className="pb-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contracts.map((contract) => {
                  const vehicleCount = vehicleInsurances.filter(vi => vi.contract_id === contract.id).length;
                  const contractPremium = sumScope(
                    vehicleInsurances.filter(vi => vi.contract_id === contract.id),
                    vi => vi.premium_amount
                  );
                  const intermediary = getIntermediary(contract.intermediary_id);
                  const daysLeft = contract.expiry_date
                    ? Math.ceil((new Date(contract.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
                    : null;

                  return (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{contract.policy_number}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded">
                            {(contract.insurer_name || t('common.states.notAvailable')).substring(0, 3).toUpperCase()}
                          </span>
                          <span className="text-gray-700">{contract.insurer_name || '—'}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-600">
                        {intermediary?.name || '—'}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                          <Car className="w-3.5 h-3.5" />
                          {vehicleCount}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div>
                          <span className={`text-sm ${daysLeft !== null && daysLeft <= 90 ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>
                            {contract.expiry_date ? fmt.date(contract.expiry_date) : '—'}
                          </span>
                          {daysLeft !== null && daysLeft <= 90 && (
                            <span className="block text-xs text-orange-500">{t('insurance.daysLeft', { n: daysLeft })}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-right font-medium text-gray-900">
                        {contractPremium.label}
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => onViewContract(contract)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('insurance.viewContract')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {contracts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('insurance.activeContractsModal.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActiveContractsModal;
